/**
 * ExecutionAgent - Context Engineering Note
 * 
 * Guidance on context IDs and their usage.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { CONTEXT_ID_PREFIXES } from '../../../base/systemPrompt/shared';
import { EXECUTION_AGENT_PROMPT_KEYS } from './variables';

export const EXECUTION_AGENT_CONTEXT_PREFIXES: (keyof typeof CONTEXT_ID_PREFIXES)[] = [
    'slide',
    'alphasense',
    'logo',
    'wikimedia',
    'asset',
    'artifact',
    'excelRead',
    'excel',
];

class ExecutionAgentContextNoteSection extends PromptSectionBase {
    protected readonly keywordsKey = EXECUTION_AGENT_PROMPT_KEYS.contextNote;
    protected readonly fallbackContent = `## Context Engineering

Context items are passed with specific IDs that you must use correctly to link dynamic content.

### Context ID Prefixes

- **slide_xxx**: Slide data from readSlide (shapes, layout, content)
- **alphasense_xxx**: AlphaSense research results (earnings, filings, broker reports)
- **logo_xxx**: Brand logos from Brandfetch (company logos)
- **wikimedia_xxx**: Wikimedia images (stock photos, diagrams)
- **asset_xxx**: Presentation assets (embedded images, media)
- **artifact_xxx**: Generated artifacts (charts, tables for insertion)
- **excelread_xxx**: Excel read results with cell values and formulas
- **excel_xxx**: Excel workbook data and structure

### Using Context IDs

1. **Reference by ID**: When passing context to sub-agents, use the exact ID
   \`\`\`json
   { "context": ["as_001", "logo_acme", "slide_5"], "purpose": "Research, branding, and reference slide" }
   \`\`\`

2. **Include purpose**: Always explain what the context is for

3. **Group related context**: Put related items in the same context array

4. **Don't fabricate IDs**: Only use IDs returned from tools/searches

### Context in ExecutionAgent

You receive context items with specific IDs. Here's how to use them:

**Image Context** (logo_xxx, wikimedia_xxx, asset_xxx, artifact_xxx):
- These are displayed as images in your initial message
- Use them in shape fills or as image objects
- Reference by ID in your buildArea calls

**Text Context** (as_xxx, excelread_xxx, page_xxx):
- These provide data, research findings, or document content
- Use the information to populate text shapes, tables, or charts
- Include citations/sources when using research data

**Excel Context** (artifact_xxx, excelread_xxx):
- artifact_xxx: Chart images from Excel
- excelread_xxx: Data ranges you can reference
- Use these IDs when creating linked charts or tables

**Reference Slides** (slide_N):
- These are style guide slides for visual reference
- Shown as images in your initial message
- Study them for styling patterns and layout conventions`.trim();
}

const contextNoteSection = new ExecutionAgentContextNoteSection();

/**
 * Get the context note section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getContextNoteSection(): string {
    return contextNoteSection.getContent();
}

/**
 * Export constant for backwards compatibility
 */
export const EXECUTION_AGENT_CONTEXT_NOTE = contextNoteSection.getFallbackContent();
